import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Bell, CalendarCheck, Trophy, Award, MessageSquare, Dumbbell, Megaphone, Swords, UserPlus, ShieldQuestion, Crown } from "lucide-react";

const TYPE_ICONS = {
  booking: CalendarCheck, tournament: Trophy, league: Award,
  comment: MessageSquare, training: Dumbbell, announcement: Megaphone, challenge: Swords,
  follow: UserPlus, request: ShieldQuestion, gold: Crown,
};

function timeAgo(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsPanel({ open, onClose, onRead, onOpenMessages }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setItems(data);
    await api.post("/notifications/read-all");
    onRead?.();
  }, [onRead]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const go = (n) => {
    onClose();
    if (!n.link) return;
    if (n.link.includes("open=messages")) { onOpenMessages?.(); return; }
    navigate(n.link);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80]" onClick={onClose}>
      <div className="absolute top-16 right-3 w-[min(92vw,380px)] max-h-[420px] bg-pb-surface border border-pb-border shadow-[0_12px_40px_rgba(0,0,0,0.6)] flex flex-col animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="notifications-panel">
        <div className="flex items-center gap-3 p-3 border-b border-pb-border shrink-0">
          <Bell className="w-5 h-5 text-pb-red" />
          <span className="font-display font-black uppercase text-lg flex-1">Notifications</span>
          <button data-testid="notifications-close-btn" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="max-h-[264px] overflow-y-auto divide-y divide-pb-border/50" data-testid="notifications-list">
          {items.length === 0 && <div className="p-6 text-sm text-zinc-600 text-center">You're all caught up — nothing new yet.</div>}
          {items.map((n) => {
            const Icon = TYPE_ICONS[n.type] || Bell;
            const gold = n.type === "gold";
            return (
              <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => go(n)}
                className={`w-full flex gap-3 px-4 py-3 text-left transition-colors ${
                  gold ? "border-2 border-yellow-500/80 bg-gradient-to-r from-yellow-500/10 to-transparent shadow-[0_0_16px_rgba(212,175,55,0.35)] my-1 mx-1 w-[calc(100%-8px)] hover:bg-yellow-500/10"
                       : "hover:bg-pb-elevated"} ${n.read ? "opacity-60" : ""}`}>
                <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${
                  gold ? "bg-yellow-500/15 border border-yellow-500/70" : "bg-pb-red/10 border border-pb-red/30"}`}>
                  <Icon className={`w-4 h-4 ${gold ? "text-yellow-400" : "text-pb-red"}`}
                    style={gold ? { filter: "drop-shadow(0 0 6px rgba(212,175,55,.8))" } : undefined} />
                </div>
                <div className="flex-1 min-w-0">
                  {gold && <div className="text-[8px] font-black uppercase tracking-[0.3em] text-yellow-400 mb-0.5">Gold Letter · Special Transmission</div>}
                  <div className="text-sm font-semibold leading-snug">{n.title}</div>
                  {n.body && <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${gold ? "bg-yellow-400" : "bg-pb-red"}`} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
